import { useState } from 'react';
import { Container, Row, Col, ProgressBar, Card } from 'react-bootstrap';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../../components/Modal/CustomModal.jsx';
import SpinnerButton from '../../../components/Button/SpinnerButton';
import useSubscriptionStore from '../../../stores/subscriptionStore.js';
import { authFetch } from '../../../utils/apiClient.js';
import useProductStore from '../../../stores/productStore.js';
import useUsageStore from '../../../stores/usageStore.js';

export default function SubscriptionsScreen() {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const { subscription, isUltimate } = useSubscriptionStore();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { currentProduct } = useProductStore();
    const { usage } = useUsageStore();

    const getSubscriptionName = () => {
        return currentProduct?.name || t('subscriptions.plans.free');
    };

    const getMonthlyLimit = () => {
        return currentProduct?.metadata?.limit || null;
    };

    const getUsagePercentage = () => {
        const limit = getMonthlyLimit();
        const limitAsNumber = parseInt(limit, 10);
        return Math.round(limitAsNumber - usage?.usage)
    };

    const isCancelAtPeriodEnd = subscription?.cancelAtPeriodEnd;

    const handleCancel = async (cancelAtPeriodEnd) => {
        setIsLoading(true);
        try {
            await authFetch('/api/subscriptions/cancel', {
                method: 'POST',
                body: JSON.stringify({
                    subscriptionId: subscription?.id,
                    cancelAtPeriodEnd: cancelAtPeriodEnd
                }),
            });
        } catch (error) {
            console.error('Error cancelling subscription:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelClick = () => {
        if (isCancelAtPeriodEnd) {
            handleCancel(false);
        } else {
            setShowCancelModal(true);
        }
    };

    const confirmCancel = () => {
        setShowCancelModal(false);
        handleCancel(true);
    };

    return (
        <Container className="py-5">
            <Row className="mb-5">
                <Col xs={12} className="text-center">
                    <h2 className="title mb-2">{t('subscriptions.title')}</h2>
                    <p className="subtitle">{t('subscriptions.subtitle')}</p>
                </Col>
            </Row>

            <Card>
                <Card.Body>
                    <Row className="mb-4 g-4">
                        <Col xs={12} md={6}>
                            <h3 className="card-title mb-3">{t('subscriptions.currentPlan')}</h3>
                            <p className="card-text">{getSubscriptionName()}</p>
                        </Col>

                        {subscription && (
                            <Col xs={12} md={6}>
                                <h3 className="card-title mb-3">{t('subscriptions.status')}</h3>
                                <p className="card-text">
                                    {isCancelAtPeriodEnd ? t('subscriptions.cancellationRequested') : t(`pricing.status.${subscription?.status}`)}
                                </p>
                            </Col>
                        )}
                    </Row>

                    {getMonthlyLimit() && (
                        <Row className="g-4">
                            <Col xs={12} md={6}>
                                <h3 className="card-title mb-3">{t('subscriptions.generationsPerMonth')}</h3>
                                <ProgressBar now={getUsagePercentage()} max={parseInt(getMonthlyLimit(), 10)} label={`${getUsagePercentage()}`} />
                            </Col>
                        </Row>
                    )}

                    {subscription && (
                        <Row className="mt-4 border-top g-4">
                            <Col xs={12} md={6}>
                                <h3 className="card-title mb-3">{t('subscriptions.beginningPeriod')}</h3>
                                <p className="card-text">{dayjs.unix(subscription.currentPeriodStart.seconds).locale(i18n.language).format('DD MMMM YYYY HH:mm')}</p>
                            </Col>
                            <Col xs={12} md={6}>
                                <h3 className="card-title mb-3">{t('subscriptions.EndPeriod')}</h3>
                                <p className="card-text">{dayjs.unix(subscription.currentPeriodEnd.seconds).locale(i18n.language).format('DD MMMM YYYY HH:mm')}</p>
                            </Col>
                        </Row>
                    )}

                    <Row className="mt-4 border-top g-4">
                        <Col xs={12} md={6}>
                            {!isUltimate && (
                                <SpinnerButton
                                    className="bg-pink w-100"
                                    onClick={() => navigate('/pricing')}
                                    label={t('subscriptions.upgradePlan')}
                                />
                            )}

                        </Col>
                        <Col xs={12} md={6}>
                            {subscription && (
                                <SpinnerButton
                                    className="w-100"
                                    isLoading={isLoading}
                                    variant={isCancelAtPeriodEnd ? "outline-success" : "outline-danger"}
                                    onClick={handleCancelClick}
                                    label={isCancelAtPeriodEnd ? t('subscriptions.renewSubscription') : t('subscriptions.cancelSubscription')}
                                />
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <CustomModal
                show={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                title={t('subscriptions.cancelConfirmTitle')}
                messageHtml={t('subscriptions.cancelConfirmMessage')}
                actionLabel={t('subscriptions.cancelConfirmTitle')}
                actionVariant="secondary"
                closeVariant="primary"
                closeClassName="bg-pink"
                onAction={confirmCancel}
            />
        </Container >
    );
}
